import { Command } from "commander";
import { writeFileSync, existsSync } from "node:fs";
import { resolve } from "node:path";
import chalk from "chalk";
import { loadData } from "@fathom/core";
import { getConvexClient, logConvexStatus } from "../convex-client.js";
import { api } from "../../../../convex/_generated/api.js";
import {
  fetchPricing,
  fetchIntelligence,
  mergePricingData,
  type MergeResult,
} from "../fetchers/index.js";
import { generateModelsYaml, generateAgentsYaml } from "../fetchers/merge.js";
import { withSpinner } from "../ux/spinner.js";
import { warnAction } from "../ux/errors.js";

function findDataDir(): string {
  const bundled = resolve(import.meta.dirname ?? __dirname, "data");
  if (existsSync(resolve(bundled, "models.yaml"))) return bundled;
  return resolve(import.meta.dirname ?? __dirname, "../../../../data");
}

interface ResearchOptions {
  update?: boolean;
  autoUpdate?: boolean;
  sync?: boolean;
  check?: boolean;
  offline?: boolean;
  verbose?: boolean;
}

export const researchCommand = new Command("research")
  .description("Check and update model pricing data (fetches live data)")
  .option("--update", "Update local data files with latest pricing")
  .option("--auto-update", "Fetch, merge, and write updated pricing non-interactively")
  .option("--sync", "Also sync updated pricing to Convex")
  .option("--check", "Check for differences without updating (default)")
  .option("--offline", "Skip remote fetches, use local data only")
  .option("--verbose", "Show fetch sources and timing details")
  .action(async (options: ResearchOptions) => {
    // --auto-update implies --update (non-interactive)
    if (options.autoUpdate) {
      options.update = true;
    }

    console.log(chalk.bold("\nFathom — Research: Model Pricing"));
    console.log(chalk.dim("─".repeat(55)));

    const startTime = Date.now();

    // Load current local data
    let localData;
    try {
      localData = loadData();
    } catch {
      warnAction(
        "Could not load local data files",
        "Proceeding with empty baseline",
        "Run `fathom init` if this is a new project",
      );
      localData = { models: [], recommendations: {} as Record<string, import("@fathom/core").AgentRecommendation> };
    }

    // Fetch pricing + intelligence in parallel
    const [pricingResult, intelligenceResult] = await withSpinner("Researching pricing intelligence...", async () =>
      Promise.all([
        fetchPricing({ offline: options.offline, verbose: options.verbose }),
        fetchIntelligence(localData.models, localData.recommendations, {
          offline: options.offline,
          verbose: options.verbose,
        }),
      ])
    );

    if (options.verbose) {
      const elapsed = Date.now() - startTime;
      console.log(chalk.dim(`\n  Fetch time: ${elapsed}ms`));
      console.log(chalk.dim(`  Pricing source: ${pricingResult.source}`));
      if (intelligenceResult) {
        console.log(chalk.dim(`  Intelligence source: ${intelligenceResult.source}`));
      }
    }

    // Source indicator
    if (pricingResult.stale) {
      console.log(chalk.yellow(`\n  ⚠ Using local data (${pricingResult.error ?? "stale"})`));
    } else {
      console.log(chalk.green(`\n  Pricing: live data (manifest v${pricingResult.data.manifestVersion}, ${pricingResult.data.manifestDate})`));
    }

    // Merge remote data with local
    const mergeResult = mergePricingData(
      localData.models,
      pricingResult.data.models,
      localData.recommendations,
      pricingResult.data.recommendations,
      intelligenceResult?.data.suggestions ?? [],
    );

    // Report pricing changes
    displayPricingReport(mergeResult);

    // Display intelligence suggestions
    if (intelligenceResult?.data) {
      displayIntelligence(intelligenceResult.data);
    }

    // Display recommendations
    displayRecommendations(mergeResult);

    // Update local files if requested
    if (options.update) {
      const dataDir = findDataDir();
      const modelsPath = resolve(dataDir, "models.yaml");
      const agentsPath = resolve(dataDir, "agents.yaml");

      writeFileSync(modelsPath, generateModelsYaml(mergeResult.models.merged));
      console.log(chalk.green(`\n  Updated: ${modelsPath}`));

      writeFileSync(agentsPath, generateAgentsYaml(mergeResult.recommendations.merged));
      console.log(chalk.green(`  Updated: ${agentsPath}`));

      // Record pricing update timestamp for staleness detection (RES-01/RES-02)
      try {
        const { recordPricingUpdate } = await import("../staleness.js");
        recordPricingUpdate();
      } catch {
        // Non-fatal — staleness tracking is optional
      }

      if (options.autoUpdate) {
        console.log(chalk.green("  Auto-update complete."));
      }
    }

    // Sync to Convex if requested
    if (options.sync) {
      await syncToConvex(mergeResult);
    }

    if (options.verbose) {
      const totalElapsed = Date.now() - startTime;
      console.log(chalk.dim(`\n  Total time: ${totalElapsed}ms`));
    }

    console.log();
  });

function displayPricingReport(result: MergeResult): void {
  const { models } = result;

  if (models.added === 0 && models.updated === 0) {
    console.log(chalk.green(`  All model pricing is up to date.`));
    console.log(chalk.dim(`  ${models.merged.length} models checked.`));
  } else {
    if (models.updated > 0) {
      console.log(chalk.yellow(`\n  ${models.updated} pricing change(s) found:`));
      for (const change of models.changes) {
        if (change.status === "updated" && change.fields) {
          for (const f of change.fields) {
            console.log(chalk.dim(`    ${change.name}: ${f.field} ${f.old} → ${f.new}`));
          }
        }
      }
    }

    if (models.added > 0) {
      console.log(chalk.yellow(`\n  ${models.added} new model(s) found:`));
      for (const change of models.changes) {
        if (change.status === "added") {
          const m = models.merged.find((x) => x.id === change.id);
          if (m) {
            console.log(
              chalk.dim(`    ${m.name} (${m.tier}): $${m.input_per_mtok}/$${m.output_per_mtok} per MTok`),
            );
          }
        }
      }
    }
  }
}

function displayIntelligence(intelligence: { suggestions: Array<{ type: string; model?: string; description: string; confidence: string }>; summary: string }): void {
  if (intelligence.suggestions.length === 0) {
    console.log(chalk.dim(`\n  Intelligence: ${intelligence.summary}`));
    return;
  }

  console.log(chalk.cyan(`\n  Intelligence suggestions (${intelligence.suggestions.length}):`));
  for (const s of intelligence.suggestions) {
    const badge =
      s.confidence === "high" ? chalk.red("HIGH") :
      s.confidence === "medium" ? chalk.yellow("MED") :
      chalk.dim("LOW");
    const model = s.model ? chalk.dim(` [${s.model}]`) : "";
    console.log(`    ${badge} ${s.description}${model}`);
  }
  console.log(chalk.dim(`  Summary: ${intelligence.summary}`));
  console.log(chalk.dim(`  Note: Task profiles are team-calibrated and shown as suggestions only.`));
}

function displayRecommendations(result: MergeResult): void {
  const { recommendations } = result;

  if (recommendations.updated > 0 || recommendations.added > 0) {
    console.log(chalk.yellow(`\n  Recommendation changes:`));
    for (const change of recommendations.changes) {
      if (change.status === "updated") {
        console.log(chalk.dim(`    ${change.category}: ${change.oldModel} → ${change.newModel}`));
      } else if (change.status === "added") {
        console.log(chalk.dim(`    ${change.category}: ${change.newModel} (new)`));
      }
    }
  }

  console.log(chalk.dim("\n  Current model recommendations:"));
  console.log(chalk.dim("    S/M complexity → Haiku 4.5 ($1/$5 per MTok) — fast, cheap"));
  console.log(chalk.dim("    L complexity   → Sonnet 4.6 ($3/$15 per MTok) — balanced"));
  console.log(chalk.dim("    XL complexity  → Opus 4.6 ($15/$75 per MTok) — highest quality"));
  console.log(
    chalk.dim("\n    Tip: Enable prompt caching to save 90% on input tokens for repeated context."),
  );
}

async function syncToConvex(result: MergeResult): Promise<void> {
  const client = getConvexClient();
  if (!client) {
    logConvexStatus(false);
    return;
  }

  let synced = 0;
  for (const m of result.models.merged) {
    try {
      await client.mutation(api.modelPricing.upsert, {
        model: m.name,
        provider: m.provider,
        inputPerMTok: m.input_per_mtok,
        outputPerMTok: m.output_per_mtok,
        cacheReadPerMTok: m.cache_read_per_mtok,
        batchDiscount: m.batch_discount,
      });
      synced++;
    } catch {
      // skip individual failures
    }
  }
  logConvexStatus(synced > 0);
  if (synced > 0) {
    console.log(chalk.dim(`  ${synced} models synced to Convex.`));
  }
}
